import {
  Box,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { colors } from '@src/styles';
import { RunSearchItemDTO } from '@src/services/automations/modules/v1/requests/dto/requests.search.response.dto';
import { StatusChip } from './statusChip';
import { LogsTableSkeleton } from './LogsTableSkeleton';
import { footnoteSX, monoFontFamily, paginationSX } from '../utils/format';

type SortBy = 'createdAt' | 'flowName' | 'status';

const COLS: { key: SortBy; label: string }[] = [
  { key: 'flowName', label: 'Automação' },
  { key: 'status', label: 'Status' },
  { key: 'createdAt', label: 'Início' },
];

const th = {
  fontFamily: monoFontFamily,
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  color: colors.brand.textMuted,
  borderBottom: `1px solid ${colors.brand.line}`,
  fontWeight: 500,
  paddingY: 1.25,
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: colors.brand.surface,
  '& .MuiTableSortLabel-root': { color: 'inherit' },
  '& .MuiTableSortLabel-root:hover': { color: colors.brand.textStrong },
  '& .MuiTableSortLabel-root.Mui-active': { color: colors.primary.yellow },
  '& .MuiTableSortLabel-icon': { color: `${colors.primary.yellow} !important`, fontSize: 14 },
} as const;

const td = {
  fontSize: '13px',
  color: colors.primary.white,
  borderBottom: `1px solid ${colors.brand.line}`,
  paddingY: 1.125,
} as const;

const dateCell = { ...td, fontFamily: monoFontFamily, fontSize: '11.5px', color: colors.brand.textSecondary } as const;

export function LogsTable(props: {
  items: RunSearchItemDTO[];
  total: number;
  page: number;
  pageSize: number;
  sortBy: SortBy;
  sortDir: 'asc' | 'desc';
  isLoading: boolean;
  emptyMessage?: string;
  onPageChange: (page: number) => void;
  onSortChange: (sortBy: SortBy, sortDir: 'asc' | 'desc') => void;
  onSelect: (requestId: string) => void;
}) {
  const {
    items,
    total,
    page,
    pageSize,
    sortBy,
    sortDir,
    isLoading,
    emptyMessage = 'Nenhuma execução no período.',
  } = props;
  const pages = Math.max(Math.ceil(total / pageSize), 1);

  const clickSort = (key: SortBy) => props.onSortChange(key, sortBy === key && sortDir === 'desc' ? 'asc' : 'desc');

  if (isLoading) return <LogsTableSkeleton />;
  if (!items.length)
    return (
      <Box sx={{ paddingY: 5, textAlign: 'center' }}>
        <Typography sx={{ color: colors.brand.textSecondary, fontSize: '13px' }}>{emptyMessage}</Typography>
      </Box>
    );

  return (
    <Box>
      <Box sx={{ overflow: 'auto', maxHeight: { xs: 360, md: 'min(420px, 45vh)' } }}>
        <Table size="small" stickyHeader sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              {COLS.map((c) => (
                <TableCell key={c.key} sx={th} sortDirection={sortBy === c.key ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === c.key}
                    direction={sortBy === c.key ? sortDir : 'desc'}
                    onClick={() => clickSort(c.key)}
                  >
                    {c.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={th}>Fim</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((r) => (
              <TableRow
                key={r.id}
                onClick={() => props.onSelect(r.id)}
                sx={{
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.03)' },
                  '&:last-of-type td': { borderBottom: 'none' },
                }}
              >
                <TableCell
                  sx={{
                    ...td,
                    fontWeight: 500,
                    maxWidth: 360,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {r.flowName ?? r.canonicalFlowKey ?? r.subPath ?? '—'}
                </TableCell>
                <TableCell sx={td}>
                  <StatusChip status={r.runStatus ?? 'RUNNING'} />
                </TableCell>
                <TableCell sx={dateCell}>{new Date(r.createdAt).toLocaleString('pt-BR')}</TableCell>
                <TableCell sx={dateCell}>
                  {r.runFinishedAt ? new Date(r.runFinishedAt).toLocaleString('pt-BR') : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          pt: 1.25,
          mt: 0.5,
          borderTop: `1px solid ${colors.brand.line}`,
        }}
      >
        <Typography sx={footnoteSX}>
          {total.toLocaleString('pt-BR')} execuções · página {page}/{pages}
        </Typography>
        <Pagination
          count={pages}
          page={page}
          onChange={(_, p) => props.onPageChange(p)}
          size="small"
          sx={paginationSX}
        />
      </Box>
    </Box>
  );
}
